import React, { useState } from 'react';
import { Reveal } from './ui/Reveal';
import { Button } from './ui/Button';
import { Users, Video, Package, UserCheck, Building2, Monitor, Globe, MapPin, Clock, Target, Calendar, Repeat, DollarSign } from 'lucide-react';
import { useSiteContent } from '../contexts/SiteContentContext';
interface TabOption {
  id: string;
  label: string;
  price: string;
  subtext?: string;
  savings?: string;
}
interface ClassCardProps {
  badgeText: string;
  badgeIcon: React.ReactNode;
  badgeBgClass: string;
  badgeColorClass: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  features: Array<{
    icon: React.ReactNode;
    text: string;
  }>;
  tabs: TabOption[];
  buttonVariant: any;
  buttonClass?: string;
  delay?: number;
  onBook: () => void;
}
function ClassCard({
  badgeText,
  badgeIcon,
  badgeBgClass,
  badgeColorClass,
  icon,
  title,
  description,
  features,
  tabs,
  buttonVariant,
  buttonClass,
  delay = 0,
  onBook
}: ClassCardProps) {
  const [activeTabId, setActiveTabId] = useState(tabs[0].id);
  const activeTab = tabs.find(t => t.id === activeTabId) || tabs[0];
  return <Reveal delay={delay} className="h-full" data-id="element-845">
      <div className="bg-white rounded-3xl p-8 border border-earth/5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md cursor-pointer relative overflow-hidden flex flex-col h-full class-card-hover" onClick={onBook} data-id="element-846">
        <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${badgeBgClass} ${badgeColorClass} mb-5 w-fit`} data-id="element-847">
          {badgeIcon}
          {badgeText}
        </div>
        <div className="w-16 h-16 bg-gradient-to-br from-sage/10 to-earth/10 rounded-2xl flex items-center justify-center mb-4" data-id="element-848">
          {icon}
        </div>
        <h3 className="font-display text-2xl text-earth mb-3" data-id="element-849">{title}</h3>
        <p className="text-stone text-sm leading-relaxed mb-6 flex-grow" data-id="element-850">
          {description}
        </p>

        <div className="flex flex-col gap-3 mb-6" data-id="element-851">
          {features.map((feat, i) => <span key={i} className="flex items-center gap-2 text-sm text-stone" data-id="element-852">
              {feat.icon}
              {feat.text}
            </span>)}
        </div>

        {/* Tabs */}
        <div className="bg-sand/50 p-1 rounded-xl flex gap-1 mb-6" data-id="element-853">
          {tabs.map(tab => <button key={tab.id} onClick={e => {
          e.stopPropagation();
          setActiveTabId(tab.id);
        }} className={`flex-1 text-xs font-semibold py-2.5 px-2 rounded-lg transition-all ${activeTabId === tab.id ? 'bg-white text-earth shadow-sm ring-1 ring-earth/5' : 'text-stone hover:text-earth hover:bg-white/50'}`} data-id="element-854">
              {tab.label}
            </button>)}
        </div>

        <div className="flex items-end mt-auto pt-4 border-t border-earth/5" data-id="element-855">
          <div data-id="element-856">
            <div className="font-display text-3xl text-earth font-bold leading-none" data-id="element-857">
              {activeTab.price}
              {activeTab.subtext && <span className="font-body text-sm font-normal text-stone ml-1" data-id="element-858">
                  {activeTab.subtext}
                </span>}
            </div>
            <div className="h-5 mt-1.5" data-id="element-859">
              {activeTab.savings && <span className="inline-block text-[11px] font-bold text-sage bg-sage/10 px-2 py-0.5 rounded-md" data-id="element-860">
                  {activeTab.savings}
                </span>}
            </div>
          </div>
        </div>
      </div>
    </Reveal>;
}
interface ClassesSectionProps {
  onBookClass: (type: 'presencial' | 'virtual') => void;
}
export function ClassesSection({
  onBookClass
}: ClassesSectionProps) {
  const {
    content
  } = useSiteContent();
  const classes = content.classes;
  return <section id="clases" className="py-24 px-6 md:px-16 bg-sand" data-id="element-861">
      <div className="max-w-7xl mx-auto" data-id="element-862">
        <Reveal className="text-center mb-16" data-id="element-863">
          <span className="inline-block bg-sage-light/30 text-earth text-xs font-semibold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4" data-id="element-864">
            {classes.sectionLabel}
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-earth mb-4" data-id="element-865">
            {classes.sectionTitle}
          </h2>
          <p className="text-stone text-lg max-w-2xl mx-auto" data-id="element-866">
            {classes.sectionSubtitle}
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-867">
          {/* Presencial */}
          <ClassCard delay={100} badgeText="Presencial" badgeIcon={<Building2 className="w-3.5 h-3.5" data-id="element-869" />} badgeBgClass="bg-presencial-light" badgeColorClass="text-sage" icon={<Users className="w-8 h-8 text-sage" data-id="element-870" />} title={classes.presencial.title} description={classes.presencial.description} features={[{
          icon: <Clock className="w-4 h-4" data-id="element-871" />,
          text: '60 minutos'
        }, {
          icon: <MapPin className="w-4 h-4" data-id="element-872" />,
          text: 'Varias ubicaciones'
        }, {
          icon: <Users className="w-4 h-4" data-id="element-873" />,
          text: 'Cupo limitado'
        }]} tabs={[{
          id: 'single',
          label: '1 Clase',
          price: classes.presencial.singlePrice
        }, {
          id: 'pack4',
          label: '4 Clases',
          price: classes.presencial.packPrice,
          savings: classes.presencial.packSavings
        }]} buttonVariant="sage" onBook={() => onBookClass('presencial')} data-id="element-868" />

          {/* Virtual */}
          <ClassCard delay={200} badgeText="Virtual" badgeIcon={<Monitor className="w-3.5 h-3.5" data-id="element-875" />} badgeBgClass="bg-virtual-light" badgeColorClass="text-virtual" icon={<Video className="w-8 h-8 text-virtual" data-id="element-876" />} title={classes.virtual.title} description={classes.virtual.description} features={[{
          icon: <Clock className="w-4 h-4" data-id="element-877" />,
          text: '60 minutos'
        }, {
          icon: <Monitor className="w-4 h-4" data-id="element-878" />,
          text: 'Zoom en vivo'
        }, {
          icon: <Globe className="w-4 h-4" data-id="element-879" />,
          text: 'Desde cualquier lugar'
        }]} tabs={[{
          id: 'single',
          label: '1 Clase',
          price: classes.virtual.singlePrice
        }, {
          id: 'pack4',
          label: '4 Clases',
          price: classes.virtual.packPrice,
          savings: classes.virtual.packSavings
        }]} buttonVariant="outline" buttonClass="border-virtual text-virtual hover:bg-virtual-light hover:border-virtual" onBook={() => onBookClass('virtual')} data-id="element-874" />

          {/* Privadas */}
          <ClassCard delay={300} badgeText="Privada" badgeIcon={<UserCheck className="w-3.5 h-3.5" data-id="element-881" />} badgeBgClass="bg-earth/10" badgeColorClass="text-earth" icon={<UserCheck className="w-8 h-8 text-earth" data-id="element-882" />} title={classes.privada.title} description={classes.privada.description} features={[{
          icon: <Clock className="w-4 h-4" data-id="element-883" />,
          text: '60 minutos'
        }, {
          icon: <MapPin className="w-4 h-4" data-id="element-884" />,
          text: 'Presencial o virtual'
        }, {
          icon: <Target className="w-4 h-4" data-id="element-885" />,
          text: 'Práctica a medida'
        }]} tabs={[{
          id: 'single',
          label: '1 Clase',
          price: classes.privada.singlePrice
        }, {
          id: 'pack4',
          label: '4 Clases',
          price: classes.privada.packPrice,
          savings: classes.privada.packSavings
        }]} buttonVariant="primary" buttonClass="bg-earth hover:bg-earth/90 text-cream" onBook={() => onBookClass('presencial')} data-id="element-880" />
        </div>
      </div>
    </section>;
}